import React, { useState, useEffect } from "react";

// Channel picker for notifying one contact from the booth popup.
//
// Sending is not wired to a backend yet: Send hands the request to
// onSend (see SvgCanvas's onSendNotification prop) and closes.
const CHANNELS = [
  { key: "email", label: "Email", icon: "ri-mail-line", needs: "email" },
  { key: "sms", label: "SMS", icon: "ri-message-2-line", needs: "mobile" },
  { key: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-line", needs: "mobile" },
];

const NotifyModal = ({ target, onClose, onSend }) => {
  const [channels, setChannels] = useState([]);
  const [message, setMessage] = useState("");

  // Fresh form per recipient; Escape closes.
  useEffect(() => {
    setChannels([]);
    setMessage("");
    if (!target) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [target, onClose]);

  if (!target) return null;

  const available = (ch) => Boolean(target[ch.needs]);
  const toggle = (key) =>
    setChannels((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{ zIndex: 2000, backgroundColor: "rgba(15, 23, 42, 0.45)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-booth-popup
    >
      <div className="bg-white shadow rounded" style={{ width: "380px", maxWidth: "calc(100vw - 32px)" }} role="dialog" aria-modal="true">
        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
          <span className="fw-semibold" style={{ fontSize: "14px", color: "#007062" }}>
            <i className="ri-notification-3-line me-1"></i>Send Notification
          </span>
          <button type="button" className="btn-close" style={{ fontSize: "10px" }} onClick={onClose} aria-label="Close" />
        </div>

        <div className="px-3 py-2">
          <div className="fw-semibold text-dark" style={{ fontSize: "13px" }}>{target.name || "--"}</div>
          <div className="text-muted mb-2" style={{ fontSize: "11px" }}>
            {target.role}{target.booth ? ` · ${target.booth}` : ""}
          </div>
          <div className="small text-muted mb-3" style={{ fontSize: "11.5px" }}>
            {target.mobile && <div><i className="ri-phone-line me-1"></i>{target.mobile}</div>}
            {target.email && <div className="text-break"><i className="ri-mail-line me-1"></i>{target.email}</div>}
          </div>

          <div className="fw-semibold text-dark mb-1" style={{ fontSize: "12px" }}>Send via</div>
          <div className="d-flex gap-2 mb-3">
            {CHANNELS.map((ch) => {
              const ok = available(ch);
              const on = channels.includes(ch.key);
              return (
                <label
                  key={ch.key}
                  className="d-flex align-items-center gap-1 border rounded px-2 py-1 mb-0 flex-fill justify-content-center"
                  style={{
                    fontSize: "12.5px",
                    cursor: ok ? "pointer" : "not-allowed",
                    opacity: ok ? 1 : 0.45,
                    backgroundColor: on ? "#f0fdfa" : "#ffffff",
                    borderColor: on ? "#007062" : undefined,
                  }}
                  title={ok ? undefined : `No ${ch.needs === "email" ? "email address" : "mobile number"} for this contact`}
                >
                  <input
                    type="checkbox"
                    className="form-check-input shadow-none"
                    style={{ position: "static", float: "none", margin: 0, width: "14px", height: "14px" }}
                    checked={on}
                    disabled={!ok}
                    onChange={() => toggle(ch.key)}
                  />
                  <i className={ch.icon}></i>
                  {ch.label}
                </label>
              );
            })}
          </div>

          <div className="fw-semibold text-dark mb-1" style={{ fontSize: "12px" }}>Message</div>
          <textarea
            className="form-control shadow-none"
            rows={3}
            style={{ fontSize: "12.5px" }}
            placeholder="Type the message to send..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </div>

        <div className="d-flex justify-content-end gap-2 px-3 py-2 border-top">
          <button type="button" className="btn btn-sm btn-light border" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="btn btn-sm text-white"
            style={{ backgroundColor: "#007062" }}
            disabled={channels.length === 0}
            onClick={() => {
              if (onSend) onSend({ ...target, channels, message: message.trim() });
              onClose();
            }}
          >
            <i className="ri-send-plane-line me-1"></i>Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotifyModal;
